import * as React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IFormatter } from '../../../../../../../../Utils/Formatters/FormatterTypes';
import { ISortInfo } from '../../../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import EDTableBody from '../../../../../../../../BaseComponents/Table/Components/EDTableBody';
import EDTableCell from '../../../../../../../../BaseComponents/Table/Components/EDTableCell';
import EDTableRow from '../../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EDTypography from '../../../../../../../../BaseComponents/Typography/EDTypography';
import EDIconButton from '../../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDTooltip from '../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import {
  FieldType,
  IDetailCell,
  IDetailRow,
} from '../EkonomiskAnalysDetailTable';

interface IProps {
  ackSaldo: number;
  canViewFullVerifikationRader: boolean;
  hasPreliminaryData: boolean;
  headCells: IColumnHeader[];
  ingaendeVardeLopSaldo: number | null;
  isBudgetColumn: boolean;
  lopsaldoDetaljrad: boolean;
  page: number;
  rowsPerPage: number;
  saldoIndex: number | null;
  showVerifikation: (
    verifikationId: string,
    verifikationNr: string,
    bokfdatum: string | undefined,
    tab?:
      | 'verifikation'
      | 'fakturabild'
      | 'underlag'
      | 'referenser'
      | 'attestanter'
      | undefined
  ) => void;
  sortInfo: ISortInfo<IDetailRow>;
}

const useStyles = makeStyles()((theme: Theme) => ({
  cell: {
    padding: '4px 12px',
  },
  compactCell: {
    width: 12,
    paddingRight: 8,
    paddingLeft: 0,
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  fakturaBildCell: {
    paddingBottom: '2px',
    paddingTop: '2px',
  },
  verNrCell: {
    paddingRight: 16,
  },
  linkCell: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  rightAlignText: {
    textAlign: 'end',
  },
  definitivbokfordWarning: {
    display: 'inline-block',
    backgroundColor: theme.customProperties.Warning.main,
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  notDefinitivRowBg: {
    backgroundColor: theme.palette.grey['100'],
  },
  notDefinitivRow: {
    fontWeight: 600,
    color: theme.customProperties.Warning.dark,
  },
  detailRow: {
    height: '39px',
  },
}));

interface IColumnHeader {
  id: string;
  title1: string;
  title2: string | null;
  title3: string | null;
  formatter: IFormatter;
  isRightAligned: boolean;
  isVisible: boolean;
}

const isVerifikationCol = (cell: IDetailCell): boolean =>
  cell.fieldType === FieldType.verifikation;

const isDefinitivbokfordCol = (cell: IDetailCell): boolean =>
  cell.fieldType === FieldType.definitivbokford;

const isVisible = (cellIndex: number, headCells: IColumnHeader[]): boolean =>
  headCells[cellIndex].isVisible;

const isFakturaBildColumn = (cell: IDetailCell) =>
  cell.fieldType === FieldType.fakturabild;
const hasFakturabild = (cell: IDetailCell) => cell.value === 'Y';
const isUnderlagColumn = (cell: IDetailCell) =>
  cell.fieldType === FieldType.underlag;
const hasBokfOrderUnderlag = (cell: IDetailCell) => cell.value === 'Y';

const isAckSaldo = (cell: IDetailCell) => cell.fieldType === FieldType.ackSaldo;

const getEarlierAckSaldo = (
  saldoIndex: number | null,
  rows: IDetailRow[],
  cutOffIndex: number
): number => {
  if (saldoIndex != null) {
    const earlierAckSaldo = rows
      .slice(0, cutOffIndex)
      .reduce<number>((a, b) => a + Number(b.cells[saldoIndex].value), 0);

    return earlierAckSaldo;
  }

  return 0;
};

export const EkonomiskAnalysDetailTableBody: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles();
  const {
    canViewFullVerifikationRader,
    hasPreliminaryData,
    headCells,
    ingaendeVardeLopSaldo,
    lopsaldoDetaljrad,
    saldoIndex,
    showVerifikation,
    sortInfo,
    page,
    rowsPerPage,
    isBudgetColumn,
  } = props;

  let { ackSaldo } = props;

  return (
    <EDTableBody>
      {/* FIXME: här behövs info om ifall denna är definitivbokförd eller inte */}
      {sortInfo.rows
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row, rowIndex) => {
          return (
            <EDTableRow key={row.id} className={classes.detailRow}>
              {row.cells.map((cell, i) => {
                if (rowIndex === 0 && i === 0) {
                  ackSaldo = ingaendeVardeLopSaldo ?? 0;
                  ackSaldo += getEarlierAckSaldo(
                    saldoIndex,
                    sortInfo.rows,
                    page * rowsPerPage
                  );
                }
                if (
                  saldoIndex != null &&
                  lopsaldoDetaljrad &&
                  isAckSaldo(cell)
                ) {
                  ackSaldo += Number(row.cells[saldoIndex].value);
                }
                return (
                  isVisible(i, headCells) &&
                  !(isDefinitivbokfordCol(cell) && !hasPreliminaryData) && (
                    <EDTooltip
                      key={cell.id}
                      title={
                        isDefinitivbokfordCol(cell)
                          ? cell.value !== 'true'
                            ? 'Inte definitivbokförd'
                            : 'Definitivbokförd'
                          : headCells[i].title1 +
                            `${
                              headCells[i].title2
                                ? ' ' + headCells[i].title2
                                : ''
                            }${
                              headCells[i].title3
                                ? ' ' + headCells[i].title3
                                : ''
                            }${
                              headCells[i].title1 === 'Kommentar' &&
                              cell.value != null &&
                              isBudgetColumn
                                ? cell.value.length > 80
                                  ? `, ${cell.value}`
                                  : ''
                                : ''
                            }`
                      }
                    >
                      <EDTableCell
                        className={cx({
                          [classes.cell]: !isDefinitivbokfordCol(cell),
                          [classes.compactCell]: isDefinitivbokfordCol(cell),
                          [classes.verNrCell]: isVerifikationCol(cell),
                          [classes.notDefinitivRowBg]: !row.definitivbokford,
                          [classes.notDefinitivRow]:
                            !row.definitivbokford && isVerifikationCol(cell),
                          [classes.fakturaBildCell]: cell.icon,
                        })}
                      >
                        <span>
                          {cell.icon &&
                            isFakturaBildColumn(cell) &&
                            hasFakturabild(cell) && (
                              <EDIconButton
                                noTopMargin
                                noBottomMargin
                                noLeftMargin
                                noRightMargin
                                padding="dense"
                                tooltip=""
                                icon="image_search"
                                onClick={() =>
                                  showVerifikation(
                                    row.verifikationId,
                                    row.verifikationNr,
                                    row.bokfDatum,
                                    'fakturabild'
                                  )
                                }
                                color={
                                  canViewFullVerifikationRader
                                    ? undefined
                                    : 'primary'
                                }
                              />
                            )}
                          {cell.icon &&
                            isUnderlagColumn(cell) &&
                            hasBokfOrderUnderlag(cell) && (
                              <EDIconButton
                                noTopMargin
                                noBottomMargin
                                noLeftMargin
                                noRightMargin
                                padding="dense"
                                tooltip=""
                                icon="description"
                                onClick={() =>
                                  showVerifikation(
                                    row.verifikationId,
                                    row.verifikationNr,
                                    row.bokfDatum,
                                    'underlag'
                                  )
                                }
                                color={
                                  canViewFullVerifikationRader
                                    ? undefined
                                    : 'primary'
                                }
                              />
                            )}
                          {!cell.icon && !isDefinitivbokfordCol(cell) && (
                            <EDTypography
                              noWrap
                              className={cx({
                                [classes.rightAlignText]:
                                  headCells[i].isRightAligned,
                                [classes.linkCell]: isVerifikationCol(cell),
                                [classes.notDefinitivRow]:
                                  !row.definitivbokford &&
                                  isVerifikationCol(cell),
                              })}
                              onClick={
                                isVerifikationCol(cell)
                                  ? () =>
                                      showVerifikation(
                                        row.verifikationId,
                                        row.verifikationNr,
                                        row.bokfDatum,
                                        'verifikation'
                                      )
                                  : undefined
                              }
                            >
                              {headCells[i].formatter.displayer.display(
                                isAckSaldo(cell)
                                  ? ackSaldo.toString()
                                  : headCells[i].title1 === 'Kommentar' &&
                                    cell.value != null &&
                                    isBudgetColumn
                                  ? cell.value.length > 80
                                    ? `${cell.value.slice(0, 80)}...`
                                    : cell.value
                                  : cell.value,
                                true
                              )}
                            </EDTypography>
                          )}
                          {isDefinitivbokfordCol(cell) &&
                            cell.value !== 'true' && (
                              <span
                                className={classes.definitivbokfordWarning}
                              />
                            )}
                        </span>
                      </EDTableCell>
                    </EDTooltip>
                  )
                );
              })}
            </EDTableRow>
          );
        })}
    </EDTableBody>
  );
};
export default EkonomiskAnalysDetailTableBody;
